import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  MdCalendarToday, MdChevronLeft, MdChevronRight, MdAdd,
  MdCheckCircle, MdRadioButtonUnchecked, MdDelete, MdAlarm
} from 'react-icons/md';
import { recordatoriosAPI } from '../services/api';
import { useModal } from '../context/ModalContext';

/* ─── styled components ─── */
const Section = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
  margin-top: ${p => p.theme.spacing.xl};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const SectionTitle = styled.h3`
  font-size: ${p => p.theme.fontSizes.lg};
  color: ${p => p.theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  margin: 0;
  svg { color: ${p => p.theme.colors.primary}; }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: ${p => p.theme.spacing.xl};

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

/* calendar */
const CalWrapper = styled.div``;

const CalNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing.md};
`;

const MonthLabel = styled.span`
  font-size: ${p => p.theme.fontSizes.md};
  font-weight: 700;
  color: ${p => p.theme.colors.textPrimary};
  text-transform: capitalize;
`;

const NavBtn = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  svg { font-size: 1.2rem; }
  &:hover { background: ${p => p.theme.colors.border}; color: ${p => p.theme.colors.textPrimary}; }
`;

const CalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

const DayLabel = styled.div`
  text-align: center;
  font-size: 10px;
  font-weight: 700;
  color: ${p => p.theme.colors.textLight};
  padding: 4px 0;
  text-transform: uppercase;
`;

const DayCell = styled.button`
  aspect-ratio: 1;
  border: none;
  border-radius: 8px;
  background: ${p =>
    p.selected ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})` :
    p.today ? p.theme.colors.primary + '15' : 'transparent'};
  color: ${p =>
    p.selected ? 'white' :
    p.today ? p.theme.colors.primary :
    p.other ? p.theme.colors.textLight :
    p.theme.colors.textPrimary};
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: ${p => (p.selected || p.today) ? 700 : 400};
  font-family: ${p => p.theme.fonts.primary};
  cursor: ${p => p.other ? 'default' : 'pointer'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: ${p => p.theme.transitions.fast};
  gap: 1px;

  &:hover:not(:disabled) {
    background: ${p => p.selected
      ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
      : p.theme.colors.background};
  }
`;

const DotRow = styled.div`
  display: flex;
  gap: 2px;
  justify-content: center;
`;

const Dot = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${p => p.done ? p.theme.colors.success : (p.white ? 'white' : p.theme.colors.primary)};
`;

/* reminders panel */
const Panel = styled.div``;

const PanelDate = styled.h4`
  font-size: ${p => p.theme.fontSizes.md};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing.md} 0;
`;

const AddForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.sm};
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const AddRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
`;

const SaveBtn = styled.button`
  padding: 8px 16px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  white-space: nowrap;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const CancelBtn = styled.button`
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: none;
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textSecondary};
  cursor: pointer;
  font-family: ${p => p.theme.fonts.primary};
`;

const NewBtn = styled.button`
  width: 100%;
  padding: 9px;
  border: 1.5px dashed ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: none;
  color: ${p => p.theme.colors.textSecondary};
  font-size: ${p => p.theme.fontSizes.sm};
  font-family: ${p => p.theme.fonts.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  margin-bottom: ${p => p.theme.spacing.lg};
  transition: ${p => p.theme.transitions.fast};
  svg { font-size: 1.1rem; }
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;

const ReminderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.sm};
  max-height: 320px;
  overflow-y: auto;
`;

const ReminderItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.md};
  background: ${p => p.done ? p.theme.colors.background : p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.done ? p.theme.colors.borderLight : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  opacity: ${p => p.done ? 0.65 : 1};
  transition: ${p => p.theme.transitions.fast};
`;

const CheckBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: ${p => p.done ? p.theme.colors.success : p.theme.colors.textLight};
  svg { font-size: 1.25rem; }
  flex-shrink: 0;
  margin-top: 1px;
  &:hover { color: ${p => p.done ? p.theme.colors.success : p.theme.colors.primary}; }
`;

const ReminderContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ReminderTitle = styled.p`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
  text-decoration: ${p => p.done ? 'line-through' : 'none'};
`;

const ReminderTime = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  display: flex;
  align-items: center;
  gap: 3px;
  svg { font-size: 0.85rem; }
`;

const DelBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  color: ${p => p.theme.colors.textLight};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  svg { font-size: 1rem; }
  &:hover { color: ${p => p.theme.colors.danger}; }
`;

const EmptyMsg = styled.p`
  color: ${p => p.theme.colors.textLight};
  font-size: ${p => p.theme.fontSizes.sm};
  text-align: center;
  padding: ${p => p.theme.spacing.lg};
  margin: 0;
`;

/* ─── helpers ─── */
const DAYS_ES = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
const MONTHS_ES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'
];

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const todayStr = toDateStr(new Date());

/* ─── component ─── */
const CalendarioRecordatorios = ({ entidadTipo, entidadId }) => {
  const { confirm, toast } = useModal();
  const [recordatorios, setRecordatorios] = useState([]);
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [showForm, setShowForm] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [hora, setHora] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, [entidadTipo, entidadId]); // eslint-disable-line

  const load = async () => {
    try {
      const res = await recordatoriosAPI.getAll({ entidad_tipo: entidadTipo, entidad_id: entidadId });
      setRecordatorios(res.data.data || []);
    } catch {
      setRecordatorios([]);
    }
  };

  /* calendar grid */
  const buildGrid = () => {
    const y = viewDate.getFullYear();
    const m = viewDate.getMonth();
    const first = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const prevDays = new Date(y, m, 0).getDate();
    const cells = [];
    for (let i = first - 1; i >= 0; i--) cells.push({ day: prevDays - i, cur: false });
    for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, cur: true });
    while (cells.length % 7 !== 0) cells.push({ day: cells.length - daysInMonth - first + 2, cur: false });
    return cells;
  };

  const grid = buildGrid();

  const getDateStr = (cell) => {
    if (!cell.cur) return null;
    const y = viewDate.getFullYear();
    const m = String(viewDate.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-${String(cell.day).padStart(2, '0')}`;
  };

  const remindersForDate = (dateStr) =>
    recordatorios.filter(r => r.fecha_recordatorio?.startsWith(dateStr));

  const selectedReminders = remindersForDate(selectedDate);

  const prevMonth = () => setViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setViewDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const handleSave = async () => {
    if (!titulo.trim()) return;
    setSaving(true);
    try {
      const fechaHora = hora ? `${selectedDate}T${hora}:00` : selectedDate;
      const res = await recordatoriosAPI.create({
        titulo: titulo.trim(),
        fecha_recordatorio: fechaHora,
        entidad_tipo: entidadTipo,
        entidad_id: entidadId,
      });
      setRecordatorios(prev => [...prev, res.data.data]);
      setTitulo('');
      setHora('');
      setShowForm(false);
      toast('Recordatorio creado', 'success');
    } catch (err) {
      toast(err.message || 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (r) => {
    try {
      const res = await recordatoriosAPI.toggleCompletado(r.id);
      setRecordatorios(prev => prev.map(x => x.id === r.id ? res.data.data : x));
    } catch (err) {
      toast(err.message || 'Error', 'error');
    }
  };

  const handleDelete = async (r) => {
    const ok = await confirm(`¿Eliminar "${r.titulo}"?`, {
      title: 'Eliminar recordatorio',
      confirmText: 'Eliminar',
      danger: true
    });
    if (!ok) return;
    try {
      await recordatoriosAPI.delete(r.id);
      setRecordatorios(prev => prev.filter(x => x.id !== r.id));
      toast('Recordatorio eliminado', 'success');
    } catch (err) {
      toast(err.message || 'Error', 'error');
    }
  };

  const formatPanelDate = (str) => {
    const [y, m, d] = str.split('-').map(Number);
    return `${DAYS_ES[new Date(y, m - 1, d).getDay()]} ${d} de ${MONTHS_ES[m - 1]} ${y}`;
  };

  return (
    <Section>
      <SectionHeader>
        <SectionTitle><MdCalendarToday /> Recordatorios</SectionTitle>
      </SectionHeader>

      <Layout>
        {/* ── Calendar ── */}
        <CalWrapper>
          <CalNav>
            <NavBtn onClick={prevMonth}><MdChevronLeft /></NavBtn>
            <MonthLabel>{MONTHS_ES[viewDate.getMonth()]} {viewDate.getFullYear()}</MonthLabel>
            <NavBtn onClick={nextMonth}><MdChevronRight /></NavBtn>
          </CalNav>
          <CalGrid>
            {DAYS_ES.map(d => <DayLabel key={d}>{d}</DayLabel>)}
            {grid.map((cell, i) => {
              const ds = getDateStr(cell);
              const rems = ds ? remindersForDate(ds) : [];
              const pending = rems.filter(r => !r.completado);
              const done = rems.filter(r => r.completado);
              const isSelected = ds === selectedDate;
              const isToday = ds === todayStr;
              return (
                <DayCell
                  key={i}
                  selected={isSelected}
                  today={isToday && !isSelected}
                  other={!cell.cur}
                  disabled={!cell.cur}
                  onClick={() => cell.cur && setSelectedDate(ds)}
                >
                  {cell.day}
                  {rems.length > 0 && (
                    <DotRow>
                      {pending.slice(0, 3).map((_, j) => (
                        <Dot key={j} white={isSelected} />
                      ))}
                      {done.slice(0, 2).map((_, j) => (
                        <Dot key={`d${j}`} done white={isSelected} />
                      ))}
                    </DotRow>
                  )}
                </DayCell>
              );
            })}
          </CalGrid>
        </CalWrapper>

        {/* ── Panel ── */}
        <Panel>
          <PanelDate>{formatPanelDate(selectedDate)}</PanelDate>

          {showForm ? (
            <AddForm>
              <Input
                placeholder="Titulo del recordatorio *"
                value={titulo}
                onChange={e => setTitulo(e.target.value)}
                autoFocus
              />
              <Input
                type="time"
                value={hora}
                onChange={e => setHora(e.target.value)}
              />
              <AddRow>
                <SaveBtn onClick={handleSave} disabled={saving || !titulo.trim()}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </SaveBtn>
                <CancelBtn onClick={() => { setShowForm(false); setTitulo(''); setHora(''); }}>
                  Cancelar
                </CancelBtn>
              </AddRow>
            </AddForm>
          ) : (
            <NewBtn onClick={() => setShowForm(true)}>
              <MdAdd /> Nuevo recordatorio para esta fecha
            </NewBtn>
          )}

          <ReminderList>
            {selectedReminders.length === 0 ? (
              <EmptyMsg>Sin recordatorios para esta fecha</EmptyMsg>
            ) : (
              selectedReminders.map(r => (
                <ReminderItem key={r.id} done={r.completado}>
                  <CheckBtn done={r.completado} onClick={() => handleToggle(r)}>
                    {r.completado ? <MdCheckCircle /> : <MdRadioButtonUnchecked />}
                  </CheckBtn>
                  <ReminderContent>
                    <ReminderTitle done={r.completado}>{r.titulo}</ReminderTitle>
                    {r.fecha_recordatorio && r.fecha_recordatorio.includes('T') && (
                      <ReminderTime>
                        <MdAlarm />
                        {new Date(r.fecha_recordatorio).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                      </ReminderTime>
                    )}
                  </ReminderContent>
                  <DelBtn onClick={() => handleDelete(r)}><MdDelete /></DelBtn>
                </ReminderItem>
              ))
            )}
          </ReminderList>
        </Panel>
      </Layout>
    </Section>
  );
};

export default CalendarioRecordatorios;
